<template>
  <div class="space-y-6">
    <div>
      <h3 class="text-md font-normal">Reconhecimento facial</h3>
      <h2 class="text-sm font-normal text-gray-600">
        Esse evento usará reconhecimento facial para o controle de acesso.
      </h2>
    </div>

    <div class="space-y-6">
      <div class="mb-4 mt-1 text-sm leading-relaxed text-gray-700">
        <p>
          <b class="text-red-500 font-semibold">ATENÇÃO PARA AS INSTRUÇÕES ABAIXO:</b>
          <span class="font-semibold text-white px-1" style="background-color:#FF6600">(LEIA ATENTAMENTE)</span>
        </p>
        <p>
          <b class="text-gray-800 font-semibold">Como a foto será enviada por você, é importante seguir algumas orientações para evitar falhas na entrada.</b>
        </p>
        <br />
        <p>
          <b class="text-gray-800 font-semibold">Por quê?</b><br />
          Quando as fotos são feitas em ambientes diferentes (iluminação, câmera, ângulo, etc.), o sistema pode ter dificuldade para reconhecer o rosto.
          Seguindo as orientações abaixo, reduzimos essas chances.
        </p>

        <br />
        <p class="font-semibold text-base mt-3 text-gray-800">📸 Guia de captura da selfie</p>
        <ul class="list-disc ml-6 mt-2 space-y-1">
          <li>Olhe diretamente para a câmera (rosto centralizado).</li>
          <li>Tire a foto em um ambiente bem iluminado, de preferência com luz natural de frente.</li>
          <li>Mantenha o rosto neutro, boca fechada e olhos abertos.</li>
          <li>Evite sombras (não fique contra a luz).</li>
          <li>Sem óculos escuros, bonés ou máscaras.</li>
          <li>Não use filtros ou modo retrato do celular.</li>
          <li>Fundo sólido, sem pessoas atrás.</li>
          <li>Vire a cabeça para a direita (como se estivesse olhando para o lado) e repita o movimento para a esquerda.</li>
        </ul>

        <br />
        <p class="font-semibold text-base mt-4 text-red-500">❌ Evite:</p>
        <ul class="list-disc ml-6 mt-2 space-y-1 text-red-600">
          <li>Fotos muito próximas ou muito distantes.</li>
          <li>Cabeça inclinada ou virada de lado.</li>
          <li>Fundo escuro ou com muita luz atrás.</li>
          <li>Objetos que cubram o rosto.</li>
          <li>Pessoas no fundo.</li>
          <li>Capturar com câmeras ruins (teste se o rosto aparece nítido).</li>
        </ul>

        <br />
        <p class="font-semibold text-base mt-4 text-gray-800">📸 Antes de tirar sua selfie:</p>
        <ul class="list-disc ml-6 mt-2 space-y-1">
          <li>Escolha um local iluminado (sem luz e pessoas atrás de você).</li>
          <li>Olhe de frente para a câmera e mantenha o rosto dentro do círculo.</li>
          <li>Remova óculos escuros, boné, máscara, ou qualquer outro objeto que cubra o rosto.</li>
          <li>Certifique-se de que o rosto esteja totalmente visível — prenda o cabelo se ele cobrir parte do rosto ou dos olhos.</li>
        </ul>

        <div class="grid grid-cols-2 gap-6 md:grid-cols-4 py-6">
          <div class="bg-white rounded-md shadow-sm overflow-hidden border border-gray-200">
            <img
              class="w-full aspect-square object-cover"
              src="@/assets/mulher-certo.png"
              alt="Foto correta de mulher para reconhecimento facial"
            />
            <div class="p-4 text-center">
              <h3 class="text-xl font-bold text-green-600 mb-2">Correto</h3>
              <p class="text-sm text-gray-700">Rosto centralizado, expressão neutra, fundo limpo e sem obstruções.</p>
            </div>
          </div>

          <div class="bg-white rounded-md shadow-sm overflow-hidden border border-gray-200">
            <img
              class="w-full aspect-square object-cover"
              src="@/assets/mulher-errado.png"
              alt="Foto incorreta de mulher para reconhecimento facial"
            />
            <div class="p-4 text-center">
              <h3 class="text-xl font-bold text-red-600 mb-2">Incorreto</h3>
              <p class="text-sm text-gray-700">Cabelo cobrindo o rosto, sorriso e pessoas no fundo.</p>
            </div>
          </div>

          <div class="bg-white rounded-md shadow-sm overflow-hidden border border-gray-200">
            <img
              class="w-full aspect-square object-cover"
              src="@/assets/homem-certo.png"
              alt="Foto correta de homem para reconhecimento facial"
            />
            <div class="p-4 text-center">
              <h3 class="text-xl font-bold text-green-600 mb-2">Correto</h3>
              <p class="text-sm text-gray-700">Rosto centralizado, expressão neutra, fundo limpo e sem obstruções.</p>
            </div>
          </div>

          <div class="bg-white rounded-md shadow-sm overflow-hidden border border-gray-200">
            <img
              class="w-full aspect-square object-cover"
              src="@/assets/homem-errado.png"
              alt="Foto incorreta de homem para reconhecimento facial"
            />
            <div class="p-4 text-center">
              <h3 class="text-xl font-bold text-red-600 mb-2">Incorreto</h3>
              <p class="text-sm text-gray-700">Óculos escuros, sorriso e fundo complexo.</p>
            </div>
          </div>
        </div>

        <br />
        <p class="text-xs text-gray-600 mt-4">
          <b>* Nenhuma foto ou imagem sua será salva durante sua inscrição, apenas um vetor de recursos anônimo que não pode sofrer engenharia reversa.</b>
        </p>
      </div>
    </div>

    <!-- Progress Bar -->
    <div
      v-if="facialStatus === 'initializing' && !faceApiError"
      class="text-center p-4 bg-yellow-50 rounded-md space-y-3"
    >
      <p class="text-sm text-yellow-700">
        Aguarde, estamos carregando os módulos de reconhecimento facial...
      </p>
      <div class="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
        <div
          class="bg-blue-600 h-full rounded-full transition-all duration-300"
          :style="{ width: faceApiProgress + '%' }"
        ></div>
      </div>
      <p class="text-xs text-gray-600">{{ Math.round(faceApiProgress) }}% carregado</p>
    </div>

    <!-- Erro de carregamento -->
    <div v-if="facialStatus === 'error'" class="text-center p-4 bg-red-50 rounded-md">
      <p class="text-sm text-red-700">
        Erro ao carregar sistema de reconhecimento.
        <button @click="startFaceApiLoading" class="underline">Tentar novamente</button>
      </p>
    </div>

    <!-- Erro de permissão -->
    <div v-if="facialStatus === 'permission_error'" class="text-center p-4 bg-red-50 rounded-md">
      <p class="text-sm text-red-700">
        Permissão de câmera negada. Por favor, permita o acesso à câmera e recarregue a página.
      </p>
    </div>

    <!-- Botão de iniciar -->
    <div v-show="facialStatus === 'ready'">
      <div
        class="upload-box"
        @click="isFaceApiLoaded ? startFacialCapture() : null"
        :class="{ disabled: !isFaceApiLoaded }"
      >
        <div class="text-gray-600">
          <div class="text-center mb-4 d-center-flex">
            <img src="@/assets/electronics.png" width="80px" height="80px" alt="Reconhecimento facial" />
          </div>
          <p class="text-sm font-medium" :class="isFaceApiLoaded ? 'text-blue-600' : 'text-gray-400'">
            {{
              isFaceApiLoaded
                ? 'Clique aqui para iniciar o reconhecimento facial'
                : `Carregando módulos... ${Math.round(faceApiProgress)}%`
            }}
          </p>
        </div>
      </div>
    </div>

    <!-- Liveness Capture -->
    <div v-show="facialStatus === 'capturing'" class="space-y-4">
      <div class="camera-container" ref="cameraContainer">
        <video ref="video" autoplay muted playsinline class="mirror-video"></video>
        <canvas ref="canvas" class="landmarks-canvas"></canvas>
      </div>
      <div class="p-3 bg-blue-50 rounded-md text-center">
        <p class="text-sm text-blue-700 font-medium">
          {{ livenessMessage || 'Posicione seu rosto na câmera e movimente a cabeça conforme instruído...' }}
        </p>
      </div>
    </div>

    <!-- Estado: Sucesso -->
    <div v-if="facialStatus === 'captured'" class="border border-green-200 bg-green-50 rounded-md p-4 text-center">
      <div class="inline">
        <p class="text-sm font-medium text-green-700">Selfie capturada com sucesso!</p>
        <p class="text-xs text-gray-600 mt-1">Continue para o próximo passo</p>
      </div>
    </div>

    <div v-if="facialStatus === 'captured'" class="mt-4">
      <p class="text-xs text-gray-600 mb-2 text-center">Preview da selfie:</p>
      <img :src="facialPreview" class="w-full max-w-xs mx-auto rounded-lg shadow" />
    </div>

    <div v-if="facialStatus === 'captured'" class="mt-3 text-center">
      <button @click="resetFacialCapture" class="text-sm text-blue-600 hover:underline">
        Refazer captura
      </button>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import LivenessDetector from '@/services/LivenessDetector';
import FaceApiLoader from '@/services/FaceApiLoader';

export default {
  name: 'FacialRecognition',
  props: {
    submitted: {
      type: Boolean,
      default: false
    }
  },
  emits: ['capture'],
  setup(props, { emit }) {
    const facialStatus = ref('ready');
    const faceApiProgress = ref(0);
    const isFaceApiLoaded = ref(false);
    const faceApiError = ref(false);
    const facialBlob = ref(null);
    const livenessMessage = ref('');
    const video = ref(null);
    const canvas = ref(null);
    const detector = ref(null);

    const facialPreview = computed(() => {
      return facialBlob.value ? URL.createObjectURL(facialBlob.value) : '';
    });

    const startFaceApiLoading = async () => {
      facialStatus.value = 'initializing';
      faceApiError.value = false;
      faceApiProgress.value = 0;
      isFaceApiLoaded.value = false;

      try {
        await FaceApiLoader.loadModels();

        const interval = setInterval(() => {
          faceApiProgress.value = FaceApiLoader.getProgress();
          isFaceApiLoaded.value = FaceApiLoader.isLoaded();

          if (FaceApiLoader.isLoaded()) {
            clearInterval(interval);
            facialStatus.value = 'ready';
            isFaceApiLoaded.value = true;
          }
        }, 100);
      } catch (err) {
        console.error('Erro ao carregar modelos:', err);
        faceApiError.value = true;
        facialStatus.value = 'error';
      }
    };

    const stopCamera = () => {
      if (video.value?.srcObject) {
        video.value.srcObject.getTracks().forEach((t) => t.stop());
        video.value.srcObject = null;
      }
    };

    const startFacialCapture = async () => {
      if (!FaceApiLoader.isLoaded()) return;

      facialStatus.value = 'capturing';
      facialBlob.value = null;

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: 640, height: 480 }
        });

        video.value.srcObject = stream;

        await new Promise((resolve) => {
          video.value.onloadedmetadata = () => {
            video.value.play();
            canvas.value.width = video.value.videoWidth;
            canvas.value.height = video.value.videoHeight;
            resolve();
          };
        });

        detector.value = new LivenessDetector(
          video.value,
          canvas.value,
          async (canvasEl, blob, brightness) => {
            await captureAndSendFacial(canvasEl, blob, brightness);
          }
        );
        detector.value.start();
      } catch (err) {
        console.error('Erro na câmera:', err);
        facialStatus.value = 'permission_error';
      }
    };

    const captureAndSendFacial = async (canvasEl, blob, brightness) => {
      if (brightness < 40 || brightness > 200) {
        livenessMessage.value = 'Iluminação inadequada. Ajuste a luz e tente novamente.';
        setTimeout(() => resetFacialCapture(), 3000);
        return;
      }

      if (!blob || !canvasEl) {
        console.error('Captura falhou: blob ou canvas inválido');
        facialStatus.value = 'error';
        return;
      }

      facialBlob.value = blob;
      facialStatus.value = 'captured';
      livenessMessage.value = 'Facial capturada! Continue para revisão.';

      // Emite o blob para o componente pai
      emit('capture', blob);

      // Para stream
      detector.value?.stop();
      stopCamera();
    };

    const resetFacialCapture = () => {
      if (detector.value) detector.value.stop();
      stopCamera();
      facialStatus.value = 'ready';
      facialBlob.value = null;
    };

    onMounted(() => {
      // Inicia carregamento ao montar
      if (FaceApiLoader.isLoaded()) {
        facialStatus.value = 'ready';
        isFaceApiLoaded.value = true;
      } else if (!FaceApiLoader.isLoading()) {
        startFaceApiLoading();
      }
    });

    onBeforeUnmount(() => {
      if (detector.value) detector.value.stop();
      stopCamera();
    });

    return {
      facialStatus,
      faceApiProgress,
      isFaceApiLoaded,
      faceApiError,
      facialBlob,
      livenessMessage,
      video,
      canvas,
      facialPreview,
      startFaceApiLoading,
      startFacialCapture,
      resetFacialCapture
    };
  }
};
</script>

<style scoped>
.camera-container {
  position: relative;
  width: 100%;
  max-width: 640px;
  margin: 0 auto;
  background: #000;
  border-radius: 12px;
  overflow: hidden;
}

.mirror-video {
  width: 100%;
  height: auto;
  transform: scaleX(-1);
}

.landmarks-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 10;
}

.upload-box {
  border: 2px dashed #94a3b8;
  padding: 30px;
  border-radius: 12px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  background: #f8fafc;
}

.upload-box:hover:not(.disabled) {
  border-color: #3b82f6;
  background: #eff6ff;
}

.upload-box.disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.d-center-flex {
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
